// PHASE_1 / B3 — typed fetch wrappers for the org & headcount endpoints.
//
// Explicit adapter signatures below describe each consumed wire contract.
// Generated OpenAPI paths exist; request/response metadata varies by endpoint,
// so these casts retain the local DTO boundary.
// Every fetch unwraps
// the spec-58 envelope through the canonical `@/api/errors` helpers; lists go
// through the keyset shape `{ items, nextCursor }`. NOTHING here carries money.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { KeysetPage } from "@/api/keyset";

// --------------------------------------------------------------------------
// Wire row shapes (mirror services/platform-graph/src/routes/org.ts DTOs)
// --------------------------------------------------------------------------

export interface OrgChartNode {
  readonly employeeId: string;
  readonly managerId: string | null;
  readonly displayName: string;
  readonly jobTitle: string | null;
  readonly departmentId: string | null;
}

export interface OrgChartResponse {
  readonly nodes: readonly OrgChartNode[];
  readonly asOfSnapshotId: string | null;
}

export interface HeadcountResponse {
  readonly tenant_id: string;
  readonly total_active: number;
  readonly by_department: readonly {
    readonly department_id: string | null;
    /** Server-side display-name join; null only when the row has no department. */
    readonly department_name: string | null;
    readonly count: number;
  }[];
}

export interface ReportingRelationshipRow {
  readonly relationship_id: string;
  readonly subordinate_employee_id: string;
  readonly reporter_employee_id: string;
  // SEM-301 display-name join — subordinate/reporter names resolved server-side
  // (PII-gated: a tombstoned subject is null). The table shows these, falling
  // back to the short id when null.
  readonly subordinate_name: string | null;
  readonly reporter_name: string | null;
  readonly kind: string;
  readonly state: string;
  readonly started_at: string | null;
  readonly ends_at: string | null;
  readonly weight_percent: string | null;
}

export interface TeamRow {
  readonly team_id: string;
  readonly name: string;
  readonly slug: string;
  readonly kind: string;
  readonly state: string;
  readonly lead_employee_id: string | null;
  // SEM-301 display-name join — the team lead's name resolved server-side
  // (PII-gated; null when unset/redacted). The table shows this, falling back to
  // the short id when null.
  readonly lead_name: string | null;
}

export interface TeamMemberRow {
  readonly membership_id: string;
  readonly team_id: string;
  readonly employee_id: string;
  readonly role: string;
  readonly allocation_percent: string | null;
  readonly state: string;
}

export interface OrgSnapshotRow {
  readonly snapshot_id: string;
  readonly captured_at: string | null;
  readonly snapshot_kind: string;
  readonly state: string;
  readonly employee_count: number;
}

// --------------------------------------------------------------------------
// Reporting-relationship create draft (the form's validated output)
// --------------------------------------------------------------------------

export interface ReportingRelationshipDraft {
  readonly subordinate_employee_id: string;
  readonly reporter_employee_id: string;
  readonly kind: string;
  readonly ends_at?: string;
  readonly weight_percent?: number;
  readonly rationale?: string;
}

// --------------------------------------------------------------------------
// Fetchers use the explicit wire signatures below.
// --------------------------------------------------------------------------

type ChartGet = (
  path: "/v1/org/chart",
  init: { params: { query: { as_of_date?: string } } },
) => Promise<EnvelopedFetchResult<OrgChartResponse>>;

export async function fetchOrgChart(asOfDate?: string): Promise<OrgChartResponse> {
  const get = platformGraph.GET as unknown as ChartGet;
  return unwrapEnvelope(
    get("/v1/org/chart", {
      params: { query: asOfDate !== undefined ? { as_of_date: asOfDate } : {} },
    }),
  );
}

type HeadcountGet = (
  path: "/v1/org/headcount",
) => Promise<EnvelopedFetchResult<HeadcountResponse>>;

export async function fetchHeadcount(): Promise<HeadcountResponse> {
  const get = platformGraph.GET as unknown as HeadcountGet;
  return unwrapEnvelope(get("/v1/org/headcount"));
}

type RelationshipsGet = (
  path: "/v1/org/reporting-relationships",
  init: { params: { query: { cursor?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<ReportingRelationshipRow>>>;

export async function fetchRelationshipsPage(
  cursor: string | null,
): Promise<KeysetPage<ReportingRelationshipRow>> {
  const get = platformGraph.GET as unknown as RelationshipsGet;
  return unwrapEnvelope(
    get("/v1/org/reporting-relationships", {
      params: { query: cursor !== null ? { cursor } : {} },
    }),
  );
}

type TeamsGet = (
  path: "/v1/org/teams",
  init: { params: { query: { cursor?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<TeamRow>>>;

export async function fetchTeamsPage(cursor: string | null): Promise<KeysetPage<TeamRow>> {
  const get = platformGraph.GET as unknown as TeamsGet;
  return unwrapEnvelope(
    get("/v1/org/teams", { params: { query: cursor !== null ? { cursor } : {} } }),
  );
}

type SnapshotsGet = (
  path: "/v1/org/snapshots",
  init: { params: { query: { cursor?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<OrgSnapshotRow>>>;

export async function fetchSnapshotsPage(
  cursor: string | null,
): Promise<KeysetPage<OrgSnapshotRow>> {
  const get = platformGraph.GET as unknown as SnapshotsGet;
  return unwrapEnvelope(
    get("/v1/org/snapshots", { params: { query: cursor !== null ? { cursor } : {} } }),
  );
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

type RelationshipPost = (
  path: "/v1/org/reporting-relationships",
  init: { body: ReportingRelationshipDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ relationship: ReportingRelationshipRow }>>;

export async function createReportingRelationship(
  draft: ReportingRelationshipDraft,
  idempotencyKey: string,
): Promise<{ relationship: ReportingRelationshipRow }> {
  const post = platformGraph.POST as unknown as RelationshipPost;
  return unwrapEnvelope(
    post("/v1/org/reporting-relationships", {
      body: draft,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

type RelationshipEndPost = (
  path: "/v1/org/reporting-relationships/:id/end",
  init: { params: { path: { id: string } }; body: { reason?: string } },
) => Promise<EnvelopedFetchResult<{ relationship: ReportingRelationshipRow }>>;

export async function endReportingRelationship(
  id: string,
  reason?: string,
): Promise<{ relationship: ReportingRelationshipRow }> {
  const post = platformGraph.POST as unknown as RelationshipEndPost;
  return unwrapEnvelope(
    post("/v1/org/reporting-relationships/:id/end", {
      params: { path: { id } },
      body: reason !== undefined ? { reason } : {},
    }),
  );
}
